import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Route, Check, RotateCcw } from 'lucide-react';
import { rt, fill } from '../lib/roundText';

// split Google Maps round persisted per context so the progress survives the jump to Maps (Android may reload the app)
const key = (ctx) => `giss_legs_${ctx}`;
export const loadRound = (ctx) => { try { const r = JSON.parse(localStorage.getItem(key(ctx))); return r && Date.now() - r.at < 2 * 864e5 ? r : null; } catch { return null; } };
const store = (ctx, r) => { localStorage.setItem(key(ctx), JSON.stringify(r)); window.dispatchEvent(new CustomEvent('giss-legs', { detail: ctx })); };
export const saveRound = (ctx, legs, extra) => {
  const slim = legs.map((l) => ({ url: l.url, n: l.stops.length }));
  const old = loadRound(ctx), same = old && old.legs.map((l) => l.url).join() === slim.map((l) => l.url).join();
  store(ctx, { ...extra, legs: slim, done: same ? old.done : [], opened: null, at: Date.now() });
};
export const clearRound = (ctx) => { localStorage.removeItem(key(ctx)); window.dispatchEvent(new CustomEvent('giss-legs', { detail: ctx })); };

function useRound(ctx) {
  const [r, setR] = useState(() => loadRound(ctx));
  useEffect(() => {
    const on = (e) => { if (!e.detail || e.detail === ctx) setR(loadRound(ctx)); };
    const back = () => {
      if (document.visibilityState !== 'visible') return;
      const c = loadRound(ctx);
      if (!c || c.opened == null) return;
      const done = [...new Set([...c.done, c.opened])], nx = c.legs.findIndex((_, i) => !done.includes(i));
      store(ctx, { ...c, done, opened: null });
      toast.success(nx < 0 ? rt().all : fill(rt().done, { i: c.opened + 1, j: nx + 1 }), { id: `legs-${ctx}`, duration: 8000 });
    };
    window.addEventListener('giss-legs', on); document.addEventListener('visibilitychange', back); window.addEventListener('focus', back);
    return () => { window.removeEventListener('giss-legs', on); document.removeEventListener('visibilitychange', back); window.removeEventListener('focus', back); };
  }, [ctx]);
  return r;
}

// leg buttons: finished legs get a tick, the next open leg is highlighted
export function LegList({ ctx, t, pre }) {
  const r = useRound(ctx), x = rt();
  if (!r || r.legs.length < 2) return null;
  const nx = r.legs.findIndex((_, i) => !r.done.includes(i));
  const open = (i) => store(ctx, { ...r, opened: i });
  const untick = (i) => store(ctx, { ...r, done: r.done.filter((d) => d !== i) });
  return (
    <div className="leg-list" data-testid={`${pre}-legs`}>
      <small className="leg-prog" data-testid={`${pre}-legs-progress`}>{nx < 0 ? x.all : fill(x.progress, { d: r.done.length, k: r.legs.length })}</small>
      {r.legs.map((l, i) => {
        const done = r.done.includes(i), cur = i === nx;
        return (
          <span key={l.url} className={`leg-item ${done ? 'done' : ''} ${cur ? 'cur' : ''}`}>
            <a className={`btn btn-sm ${cur ? 'btn-primary' : 'btn-outline'}`} data-testid={`${pre}-leg-${i}`} data-done={done} data-next={cur} href={l.url} target="_blank" rel="noreferrer" onClick={() => open(i)}>
              {done ? <Check size={13} /> : <Route size={13} />} {t.routeBtn} {i + 1}/{r.legs.length} <em>({l.n})</em>{cur && r.done.length > 0 ? ` · ${x.next}` : ''}
            </a>
            {done && <button type="button" className="leg-untick" title={x.untick} data-testid={`${pre}-leg-untick-${i}`} onClick={() => untick(i)}>×</button>}
          </span>
        );
      })}
      {r.done.length > 0 && <button type="button" className="btn btn-outline btn-sm" data-testid={`${pre}-legs-reset`} onClick={() => store(ctx, { ...r, done: [], opened: null })}><RotateCcw size={12} /> {x.reset}</button>}
    </div>
  );
}
